<template>
    <v-dialog v-model="dialog" max-width="500" @click:outside="hide">
        <v-card class="card-container">
            <v-card-title class="title-container">
                <v-icon color="#FFFFFF" @click="hide" large class="mr-3">mdi-close-circle-outline</v-icon>
                {{ $t('admin.invite_student') }}
            </v-card-title>
            <v-card-text class="mt-3">
                <v-form v-model="valid">
                    <small>{{ $t('coach.add_student_email') }}</small>
                    <kimsa-text-field
                        ref="email"
                        :rules="emailRules"
                        :placeholder="$t('entrance.email')"
                        icon="mdi-email"
                        input-mode="email"
                        @change="email = $event"
                    />
                    <v-alert v-if="errorMessage" type="error" :value="true" class="mt-2">
                        {{ errorMessage }}
                    </v-alert>
                    <template v-if="needChangePlan">
                        <v-alert v-if="isIphone" color="accent" icon="mdi-information-outline" outlined border="top">
                            {{ $t('payment.change_plan_from_desktop') }}
                        </v-alert>
                        <v-btn v-else @click="toChangePlan" color="primary" class="black--text" block depressed>
                            {{ $t('coach.update_plan') }}
                        </v-btn>
                    </template>
                </v-form>
            </v-card-text>
            <v-card-actions class="px-0 pb-0">
                <v-btn block depressed x-large color="primary" class="normal-btn no-radius black--text" :disabled="!valid"
                       @click="confirmInvitation" :loading="loading">
                    {{ $t('common.send') }}
                </v-btn>
            </v-card-actions>
        </v-card>

    </v-dialog>
</template>

<script>
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import firebase, {db} from "@/utils/Firebase";
import {mapMutations, mapState} from "vuex";

export default {
    name: "InviteStudentDialog",
    components: {KimsaTextField},
    props: {
        coach: {
            type: Object,
            default: null,
        }
    },
    data(vm) {
        return {
            dialog: false,
            valid: false,
            email: '',
            errorMessage: '',
            loading: false,
            needChangePlan: false,
            emailRules: [
                v => !!v || vm.$t('rules.required'),
                v => {
                    const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                    return pattern.test(v) || vm.$t('rules.invalid_email')
                }
            ],
        }
    },
    computed: {
        ...mapState(['dialogActive']),
        priceId(vm = this) {
            return vm.account.stripeAccount?.currentSubscription?.priceId || null
        },
        coachId(vm = this) {
            return vm.coach?.id || vm.coach?.objectID || null
        },
        coachEmail(vm = this) {
            return vm.coach?.email || vm.currentUser?.email || null
        },
        companyId(vm = this) {
            return vm.account?.coachSubscription?.companyId || vm.currentUser.uid
        }
    },
    methods: {
        ...mapMutations(['setDialogActive']),
        show() {
            const vm = this
            vm.dialog = true
            vm.setDialogActive(true)
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.setDialogActive(false)
            vm.reset()
        },
        reset() {
            const vm = this
            vm.valid = false
            vm.email = ''
            if (vm.$refs.email) vm.$refs.email.reset()
            vm.errorMessage = ''
            vm.loading = false
            vm.needChangePlan = false
        },
        inviteNewUser: firebase.functions().httpsCallable('httpInvitations-inviteStudent'),
        async getActiveStudents() {
            const vm = this
            if (vm.account?.accountType === 'coach') return vm.account?.activeStudents || 0
            let coachesRef = await db.collection('users')
                .where('coachSubscription.companyId', '==', vm.companyId).get()

            let activeStudents = 0
            coachesRef.docs.forEach(coach => {
                activeStudents += coach.data()?.activeStudents || 0
            })

            return activeStudents
        },
        async confirmInvitation() {
            const vm = this
            if (!vm.email || vm.loading) return
            if (vm.coachEmail === vm.email) return vm.errorMessage = vm.$t('coach.student_email_cant_be_same_than_yours')
            if (!vm.priceId && (vm.account?.accountType === 'coach' || vm.account?.accountType === 'company')) return vm.errorMessage = vm.$t('coach.subscription_problem')

            vm.loading = true

            if (vm.priceId) {
                const limitClientsRef = await db.collection('plans').where('priceId', '==', vm.priceId).get()
                if (limitClientsRef.docs.length > 0) {
                    const plan = await limitClientsRef.docs[0].data()
                    if (!plan.premium) {
                        let maxClients = plan.maxClients || 0
                        let activeStudents = await vm.getActiveStudents()
                        if (activeStudents >= maxClients) {
                            vm.needChangePlan = true
                            vm.loading = false
                            return vm.errorMessage = vm.$t('coach.limit_clients', {num: maxClients})
                        }
                    }
                }
            }

            const studentRef = await db.collection('users')
                .where('email', '==', vm.email)
                .get()

            if (studentRef.docs.length > 0) {
                const existingStudent = studentRef.docs[0].data()
                if (existingStudent.accountType === 'coach') {
                    vm.loading = false
                    return vm.errorMessage = vm.$t('coach.this_email_belongs_to_coach')
                }
            }

            const payload = {studentEmail: vm.email, coachId: vm.coachId}

            vm.inviteNewUser(payload).then(result => {
                console.log('invite student result', result)
                const {data: response} = result
                vm.toast(vm.$t('coach.student_invitation_sent'))
                vm.hide()
            }).catch(err => {
                vm.toast(`${err.message}.` || vm.$t('coach.student_invitation_failed'), 'error')
            }).finally(() => {
                vm.loading = false
            });
        },
        toChangePlan() {
            const vm = this
            vm.hide()
            vm.$router.push({name: `${vm.accountType}.profile.edit`, params: {mode: 'SelectPlan'}})
        },
    },
    watch: {
        dialogActive(val) {
            if (!val) this.hide()
        },
    }
}
</script>

<style scoped>

.card-container {
    border-radius: 8px;
    overflow: hidden;
}

.title-container {
    color: #FFFFFF;
    background: var(--v-backGroundPrimary-base);
}

</style>